import * as React from "react";
import { Box } from "../box/box.component";
import { LabelContext } from "../../core/label.context";
import { GeneralContext } from "../../core/general.context";
import { UserContext } from "../../core/user.context";
import { StyledRecipes } from "./recipes.style";
import { Button } from "../button/button.component";
import { LoaderComponent } from "../loader/loader.component";

interface Recipe {
    uuid: string
    title: string
    description: string
    image: string
    url: string
    course: string []
    appliances: string []
    ovencookingmethod: string []
    cuisinetype: string []
    diet: string []
    perfectfor: string []
    mainingredient: string []
    suggestedwinepairing: string []
    localizedCourse: string []
    localizedAppliances: string []
    localizedOvenCookingMethod: string []
    localizedCuisineType: string []
    localizedDiet: string []
    localizedPerfectFor: string []
    localizedMainIngredient: string []
    localizedSuggestedWinePairing: string []
    hoursTotalTime: number
    minutesTotalTime: number
    localizedDifficulty: string
    localizedTime: string
}

/**
 * Recipes COMPONENT
 * Displays user's favorite recipes
 *
 */
export const Recipes = (props: any) => {
    const { t } = React.useContext(LabelContext);
    const { userData } = React.useContext(GeneralContext);
    const { addRemoveFavoriteRecipe, getFavoriteRecipes } = React.useContext(UserContext);
    
    const favoriteRecipes = userData?.custom?.fields?.favoriteRecipes || [];
    const [recipes, setRecipes] = React.useState<Recipe[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (favoriteRecipes.length === 0) {
            setRecipes([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const subscription = getFavoriteRecipes(favoriteRecipes).subscribe(
            (response: any) => {
                if (response?.code === 200 && response?.cookings) {
                    setRecipes(response.cookings);
                } else {
                    setError(t("ecommerce.myaccount.recipes-load-error"));
                }
                setLoading(false);
            },
            () => {
                setError(t("ecommerce.myaccount.recipes-load-error"));
                setLoading(false);
            }
        );

        return () => {
            subscription?.unsubscribe?.();
        }
    }, [favoriteRecipes]);

    const handleRemoveRecipe = (recipeId: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        addRemoveFavoriteRecipe(recipeId);
    };

    return (
        <StyledRecipes>
            <div className="cooking-list">
                <div className="row">
                    <div className="col-md-12">
                        <Box className="secondary">
                            <h2>{t("ecommerce.myaccount.recipes-title")}</h2>
                            <p>{t("ecommerce.myaccount.recipes-description")}</p>
                            
                            {loading && (
                                <div className="loading-state">
                                    <LoaderComponent />
                                </div>
                            )}
                            
                            {error && (
                                <div className="error-state">
                                    <p>{error}</p>
                                </div>
                            )}
                            
                            {!loading && !error && recipes.length === 0 && (
                                <div className="empty-state">
                                    <p>{t("ecommerce.myaccount.recipes-empty-message")}</p>
                                </div>
                            )}
                            
                            {!loading && !error && recipes.length > 0 && (
                                <div className="grid stories-grid js-stories-grid mt-0 pt-0">
                                    {recipes.map((recipe: Recipe) => (
                                        <div key={recipe.uuid} className="listItem js-storyItem stories-grid__item has-edit-button">
                                            <div className="storyItem__borderBlock storyItem__borderBlock--light">
                                                <a className="recipe-link" href={recipe.url}>
                                                    <div className="storyItem__image">
                                                        <img src={recipe.image} alt={recipe.title} />
                                                    </div>
                                                    <div className="storyItem__textBlock">

                                                        <div className="quick-info-container">
                                                            {(recipe.hoursTotalTime > 0 || recipe.minutesTotalTime > 0) && (
                                                                <div className="d-flex align-items-center pr-3">
                                                                    <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <circle cx="9.99968" cy="12.4333" r="6.69792" stroke="black" strokeWidth="0.9375"/>
                                                                        <path d="M9.6875 8.7041V13.6416H13.2084" stroke="black" strokeWidth="0.865741" strokeLinecap="round"/>
                                                                        <path d="M6.25 3.3916H14.5" stroke="black" strokeWidth="0.9375" strokeLinecap="round"/>
                                                                    </svg>
                                                                    <span className="quick-info">
                                                                        <span className="pr-1">{recipe.hoursTotalTime} H</span>
                                                                        <span>{recipe.minutesTotalTime} MIN</span>
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {recipe.localizedDifficulty && (
                                                                <div className="d-flex align-items-center">
                                                                    <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <mask id="path-1-inside-1_883_3209" fill="white">
                                                                            <path fillRule="evenodd" clipRule="evenodd" d="M5.67328 11.2256C4.13249 11.0327 2.94043 9.71808 2.94043 8.125C2.94043 6.39911 4.33954 5 6.06543 5C6.1479 5 6.22963 5.00319 6.31049 5.00947C6.90795 3.54803 8.5314 2.5 10.4404 2.5C12.3495 2.5 13.9729 3.54803 14.5704 5.00947C14.6512 5.00319 14.733 5 14.8154 5C16.5413 5 17.9404 6.39911 17.9404 8.125C17.9404 9.57654 16.9508 10.7969 15.6094 11.1482L14.8154 17.5H6.06543L5.67328 11.2256Z"/>
                                                                        </mask>
                                                                        <path d="M5.67328 11.2256L6.60896 11.1672L6.5605 10.3919L5.78974 10.2954L5.67328 11.2256ZM6.31049 5.00947L6.23799 5.94416L6.91957 5.99703L7.17827 5.36423L6.31049 5.00947ZM14.5704 5.00947L13.7026 5.36424L13.9613 5.99703L14.6429 5.94416L14.5704 5.00947ZM15.6094 11.1482L15.3719 10.2413L14.7579 10.4021L14.6791 11.032L15.6094 11.1482ZM14.8154 17.5V18.4375H15.643L15.7457 17.6163L14.8154 17.5ZM6.06543 17.5L5.12976 17.5585L5.18469 18.4375H6.06543V17.5ZM5.78974 10.2954C4.71215 10.1605 3.87793 9.23944 3.87793 8.125H2.00293C2.00293 10.1967 3.55283 11.905 5.55682 12.1559L5.78974 10.2954ZM3.87793 8.125C3.87793 6.91688 4.85731 5.9375 6.06543 5.9375V4.0625C3.82177 4.0625 2.00293 5.88134 2.00293 8.125H3.87793ZM6.06543 5.9375C6.12368 5.9375 6.18122 5.93976 6.23799 5.94416L6.38299 4.07477C6.27803 4.06663 6.17212 4.0625 6.06543 4.0625V5.9375ZM7.17827 5.36423C7.61085 4.30612 8.85403 3.4375 10.4404 3.4375V1.5625C8.20877 1.5625 6.20506 2.78995 5.44271 4.6547L7.17827 5.36423ZM10.4404 3.4375C12.0268 3.4375 13.27 4.30612 13.7026 5.36424L15.4382 4.6547C14.6758 2.78995 12.6721 1.5625 10.4404 1.5625V3.4375ZM14.6429 5.94416C14.6996 5.93976 14.7572 5.9375 14.8154 5.9375V4.0625C14.7087 4.0625 14.6028 4.06663 14.4979 4.07477L14.6429 5.94416ZM14.8154 5.9375C16.0236 5.9375 17.0029 6.91688 17.0029 8.125H18.8779C18.8779 5.88134 17.0591 4.0625 14.8154 4.0625V5.9375ZM17.0029 8.125C17.0029 9.13978 16.3111 9.99535 15.3719 10.2413L15.8469 12.0552C17.5905 11.5985 18.8779 10.0133 18.8779 8.125H17.0029ZM14.6791 11.032L13.8852 17.3837L15.7457 17.6163L16.5397 11.2645L14.6791 11.032ZM14.8154 16.5625H6.06543V18.4375H14.8154V16.5625ZM7.0011 17.4415L6.60896 11.1672L4.73761 11.2841L5.12976 17.5585L7.0011 17.4415Z" fill="black" mask="url(#path-1-inside-1_883_3209)"/>
                                                                        <path d="M9.50293 11.875V14.6875M11.6904 11.875V14.6875" stroke="black" strokeWidth="0.9375" strokeLinecap="round"/>
                                                                    </svg>
                                                                    <span className="quick-info">{recipe.localizedDifficulty}</span>
                                                                </div>
                                                            )}
                                                            <button className="js_addToFavoriteRecipes btn_addToWishlist inWishlist" 
                                                                    onClick={(e) => handleRemoveRecipe(recipe.uuid, e)}>
                                                                <span className="icon"></span>
                                                            </button>
                                                        </div>

                                                        <div className="storyItem__title mb-3">
                                                            <div className="clamp">
                                                                <span className="firstLetterUppercase">{recipe.title}</span>
                                                            </div>
                                                        </div>

                                                        <ul>
                                                            {recipe.localizedDifficulty && (
                                                                <li>{recipe.localizedDifficulty}</li>
                                                            )}
                                                            {(recipe.localizedCourse.length > 0) && recipe.localizedCourse.map((course: String) => (
                                                                course && (
                                                                    <li>{course}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedAppliances.length > 0) && recipe.localizedAppliances.map((appliance: String) => (
                                                                appliance && (
                                                                    <li>{appliance}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedOvenCookingMethod.length > 0) && recipe.localizedOvenCookingMethod.map((ovenCookingMethod: String) => (
                                                                ovenCookingMethod && (
                                                                    <li>{ovenCookingMethod}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedCuisineType.length > 0) && recipe.localizedCuisineType.map((cuisine: String) => (
                                                                cuisine && (
                                                                    <li>{cuisine}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedDiet.length > 0) && recipe.localizedDiet.map((diet: String) => (
                                                                diet && (
                                                                    <li>{diet}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedPerfectFor.length > 0) && recipe.localizedPerfectFor.map((perfectFor: String) => (
                                                                perfectFor && (
                                                                    <li>{perfectFor}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedMainIngredient.length > 0) && recipe.localizedMainIngredient.map((mainIngredient: String) => (
                                                                mainIngredient && (
                                                                    <li>{mainIngredient}</li>
                                                                )
                                                            ))}
                                                            {(recipe.localizedSuggestedWinePairing.length > 0) && recipe.localizedSuggestedWinePairing.map((wine: String) => (
                                                                wine && (
                                                                    <li>{wine}</li>
                                                                )
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Box>
                    </div>
                </div>
            </div>
        </StyledRecipes>
    );
};
